import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { juntarClasses } from '../../componentes/ui';
import {
  definirData,
  formatarDataIso,
  hojeIso,
  type CardResumo,
  type Quadro,
  type TagDoCrm,
} from '../../servicos/crm';
import { ErroDaApi } from '../ErroDaApi';
import { CardDoPipeline } from './CardDoPipeline';
import {
  Arrastavel,
  CelulaDoDia,
  DIAS_DA_SEMANA,
  NavegacaoDoMes,
  PREFIXO_DO_DIA,
  colisaoDoCalendario,
  diasDaGrade,
  tituloDoMes,
  useMesDoCalendario,
} from './calendario';

/** Alvo de soltura que tira a data do card. */
const SEM_DATA = 'sem-data';

/**
 * O pipeline por data: cada card no dia da sua data de execução.
 *
 * ## Arrastar muda a data
 *
 * Soltar um card num dia grava aquele dia como data de execução; soltar em
 * "Sem data" tira a data. A tela muda na hora (`ajustes`) e a gravação vai
 * atrás; se o servidor recusar, o card volta para onde estava.
 *
 * ## Seis semanas sempre
 *
 * A grade tem 42 dias — o mês e as pontas dos vizinhos —, para a altura não
 * pular ao trocar de mês. Os dias de fora ficam apagados, mas mostram os cards
 * deles e também recebem card: um prazo no dia 1º do mês seguinte é informação
 * útil no fim deste.
 *
 * ## Quem não tem data aparece embaixo
 *
 * Um card sem data sumiria do calendário sem aviso. A lista "Sem data" é o
 * lembrete de que ele existe e ainda não tem prazo — e o lugar de onde se
 * arrasta para dar um.
 */
export function CalendarioDoPipeline(props: {
  readonly quadro: Quadro;
  readonly aoAbrirCard: (id: string) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { mes, mudar, irParaHoje } = useMesDoCalendario();
  /** Datas já trocadas na tela e ainda não confirmadas pelo servidor. */
  const [ajustes, setAjustes] = useState<Readonly<Record<string, string | null>>>({});
  const [ativo, setAtivo] = useState<string | null>(null);

  const cards = useMemo(() => props.quadro.colunas.flatMap((c) => c.cards), [props.quadro.colunas]);
  const tags = useMemo(
    () => new Map<string, TagDoCrm>(props.quadro.tags.map((t) => [t.id, t])),
    [props.quadro.tags],
  );

  // Ajuste que o servidor já confirmou sai da lista; o que ainda não chegou
  // fica, para uma resposta atrasada não devolver o card ao dia antigo.
  useEffect(() => {
    const doServidor = new Map(cards.map((c) => [c.id, c.dataExecucao]));
    setAjustes((atuais) => {
      const pendentes = Object.entries(atuais).filter(([id, data]) => doServidor.get(id) !== data);
      return pendentes.length === Object.keys(atuais).length
        ? atuais
        : Object.fromEntries(pendentes);
    });
  }, [cards]);

  const datar = useMutation({
    mutationFn: (m: { id: string; data: string | null }) => definirData(m.id, m.data),
    onError: (_erro, m) =>
      setAjustes((atuais) =>
        Object.fromEntries(Object.entries(atuais).filter(([id]) => id !== m.id)),
      ),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['crm'] }),
  });

  const dataDe = (card: CardResumo): string | null =>
    card.id in ajustes ? (ajustes[card.id] ?? null) : card.dataExecucao;

  const { porDia, semData } = useMemo(() => {
    const mapa = new Map<string, CardResumo[]>();
    const sem: CardResumo[] = [];
    for (const card of cards) {
      const data = card.id in ajustes ? (ajustes[card.id] ?? null) : card.dataExecucao;
      if (data === null) sem.push(card);
      else mapa.set(data, [...(mapa.get(data) ?? []), card]);
    }
    return { porDia: mapa, semData: sem };
  }, [cards, ajustes]);

  const sensores = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const aoSoltar = (evento: DragEndEvent): void => {
    setAtivo(null);
    const alvo = evento.over === null ? null : String(evento.over.id);
    const card = cards.find((c) => c.id === String(evento.active.id));
    if (alvo === null || card === undefined) return;

    const novaData = alvo === SEM_DATA ? null : alvo.slice(PREFIXO_DO_DIA.length);
    if (novaData === dataDe(card)) return;

    setAjustes((atuais) => ({ ...atuais, [card.id]: novaData }));
    datar.mutate({ id: card.id, data: novaData });
  };

  const nomeDe = (id: string | number): string => {
    const card = cards.find((c) => c.id === String(id));
    return card === undefined ? 'Card' : card.nomeFantasia?.trim() || card.razaoSocial;
  };
  const destinoLegivel = (id: string | number): string => {
    const alvo = String(id);
    return alvo === SEM_DATA
      ? 'sem data'
      : `dia ${formatarDataIso(alvo.slice(PREFIXO_DO_DIA.length))}`;
  };

  const hoje = hojeIso();
  const dias = diasDaGrade(mes);
  const titulo = tituloDoMes(mes);
  const cardAtivo = ativo === null ? undefined : cards.find((c) => c.id === ativo);

  return (
    <DndContext
      sensors={sensores}
      collisionDetection={colisaoDoCalendario}
      onDragStart={(e) => setAtivo(String(e.active.id))}
      onDragEnd={aoSoltar}
      onDragCancel={() => setAtivo(null)}
      accessibility={{
        announcements: {
          onDragStart: ({ active }) => `${nomeDe(active.id)} levantado.`,
          onDragOver: ({ active, over }) =>
            over === null ? undefined : `${nomeDe(active.id)} sobre ${destinoLegivel(over.id)}.`,
          onDragEnd: ({ active, over }) =>
            over === null
              ? `${nomeDe(active.id)} solto fora do calendário. A data não mudou.`
              : `${nomeDe(active.id)} agora em ${destinoLegivel(over.id)}.`,
          onDragCancel: ({ active }) => `Arrasto cancelado. ${nomeDe(active.id)} voltou.`,
        },
        screenReaderInstructions: {
          draggable:
            'Para mudar a data, aperte espaço para levantar o card, use as setas para levá-lo ao dia e espaço de novo para soltar. Esc cancela.',
        },
      }}
    >
      <div className="flex flex-col gap-6">
        {datar.isError && <ErroDaApi titulo="Não foi possível mudar a data" erro={datar.error} />}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold capitalize tracking-tight text-conteudo-primario">
              {titulo}
            </h2>
            <p className="text-xs text-conteudo-sutil">
              Arraste um card para outro dia para mudar a data de execução.
            </p>
          </div>
          <NavegacaoDoMes aoMudar={mudar} aoIrParaHoje={irParaHoje} />
        </div>

        {/* A grade rola na horizontal no celular, em vez de espremer sete colunas. */}
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <div className="grid min-w-[48rem] grid-cols-7 overflow-hidden rounded-painel border border-borda">
            {DIAS_DA_SEMANA.map((d) => (
              <div
                key={d}
                className="border-b border-borda bg-superficie-tabelaTopo px-2 py-2 text-xs font-semibold uppercase tracking-wide text-conteudo-tabelaTopo"
              >
                {d}
              </div>
            ))}
            {dias.map((d, i) => (
              <CelulaDoDia
                key={d.iso}
                iso={d.iso}
                dia={d.dia}
                doMes={d.doMes}
                hoje={d.iso === hoje}
                bordaDireita={i % 7 !== 6}
                bordaInferior={i < 35}
              >
                {(porDia.get(d.iso) ?? []).map((card) => (
                  <Arrastavel key={card.id} id={card.id} descricao="card arrastável">
                    <CardDoPipeline
                      card={{ ...card, dataExecucao: dataDe(card) }}
                      tags={tags}
                      compacto
                      aoAbrir={() => props.aoAbrirCard(card.id)}
                    />
                  </Arrastavel>
                ))}
              </CelulaDoDia>
            ))}
          </div>
        </div>

        <SemData quantidade={semData.length}>
          {semData.map((card) => (
            <Arrastavel key={card.id} id={card.id} descricao="card arrastável">
              <CardDoPipeline
                card={{ ...card, dataExecucao: null }}
                tags={tags}
                aoAbrir={() => props.aoAbrirCard(card.id)}
              />
            </Arrastavel>
          ))}
        </SemData>
      </div>

      <DragOverlay>
        {cardAtivo === undefined ? null : (
          <div className="w-44">
            <CardDoPipeline card={cardAtivo} tags={tags} compacto flutuando />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

function SemData(props: {
  readonly quantidade: number;
  readonly children: ReactNode;
}): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: SEM_DATA });

  return (
    <section
      ref={setNodeRef}
      aria-labelledby="titulo-sem-data"
      className={juntarClasses(
        'flex flex-col gap-3 rounded-painel border border-dashed p-4 transition-colors',
        isOver ? 'border-acento-ciano bg-acento-ciano/5' : 'border-borda',
      )}
    >
      <div>
        <h2 id="titulo-sem-data" className="text-sm font-semibold text-conteudo-primario">
          Sem data ({props.quantidade})
        </h2>
        <p className="text-xs text-conteudo-sutil">
          Arraste um card daqui para um dia para dar a data de execução — ou solte aqui para
          tirá-la.
        </p>
      </div>
      {props.quantidade > 0 && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">{props.children}</div>
      )}
    </section>
  );
}
